import React, { useState, useCallback } from 'react';
import { createPortal } from 'react-dom';
import { useQuery } from '@tanstack/react-query';
import { X, Search, Calendar, User, Activity, Clock, Filter, ChevronDown } from 'lucide-react';
import { auditService } from '../../services/auditService';
import { User as UserType, AuditLog } from '../../types';
import { Badge } from '../ui/badge';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';

interface UserAuditModalProps {
  user: UserType | null;
  isOpen: boolean;
  onClose: () => void;
}

const UserAuditModal: React.FC<UserAuditModalProps> = ({ user, isOpen, onClose }) => {
  const [searchTerm, setSearchTerm] = useState('');
  const [page, setPage] = useState(1);
  const [showFilters, setShowFilters] = useState(false);
  const [actionFilter, setActionFilter] = useState<string>('');
  const [entityTypeFilter, setEntityTypeFilter] = useState<string>('');
  const [startDate, setStartDate] = useState<string>('');
  const [endDate, setEndDate] = useState<string>('');
  const pageSize = 20;

  // Fetch audit logs for the specific user
  const { data: auditLogsResponse, isLoading, error } = useQuery({
    queryKey: ['userAuditLogs', user?.id, page, pageSize, searchTerm, actionFilter, entityTypeFilter, startDate, endDate],
    queryFn: () => auditService.getAuditLogs({
      userId: user?.id,
      pageNumber: page,
      pageSize,
      searchTerm: searchTerm || undefined,
      action: actionFilter || undefined,
      entityType: entityTypeFilter || undefined,
      startDate: startDate || undefined,
      endDate: endDate || undefined
    }),
    enabled: isOpen && !!user?.id,
  });

  const auditLogs = auditLogsResponse?.data?.data || [];
  const totalCount = auditLogsResponse?.data?.totalCount || 0;
  const totalPages = Math.ceil(totalCount / pageSize);

  // Fetch filter options
  const { data: actionsResponse } = useQuery({
    queryKey: ['auditLogsActions'],
    queryFn: auditService.getDistinctActions,
    staleTime: 5 * 60 * 1000,
  });

  const { data: entityTypesResponse } = useQuery({
    queryKey: ['auditLogsEntityTypes'],
    queryFn: auditService.getDistinctEntityTypes,
    staleTime: 5 * 60 * 1000,
  });

  const handleSearch = useCallback((term: string) => {
    setSearchTerm(term);
    setPage(1);
  }, []);

  const handlePageChange = useCallback((newPage: number) => {
    setPage(newPage);
  }, []);

  const clearFilters = useCallback(() => {
    setActionFilter('');
    setEntityTypeFilter('');
    setStartDate('');
    setEndDate('');
    setPage(1);
  }, []);

  const getActionBadgeColor = (action: string) => {
    const colors = {
      // Creation actions - Green
      'Create': 'bg-green-100 text-green-800 border-green-200',
      'Created': 'bg-green-100 text-green-800 border-green-200',
      'Add': 'bg-green-100 text-green-800 border-green-200',
      'Insert': 'bg-green-100 text-green-800 border-green-200',
      
      // Update actions - Accent Color
      'Update': 'bg-accent-color-100 text-accent-color-800 border-accent-color-200',
      'Updated': 'bg-accent-color-100 text-accent-color-800 border-accent-color-200',
      'Edit': 'bg-accent-color-100 text-accent-color-800 border-accent-color-200',
      'Modified': 'bg-accent-color-100 text-accent-color-800 border-accent-color-200',
      'Change': 'bg-accent-color-100 text-accent-color-800 border-accent-color-200',
      
      // Delete actions - Red
      'Delete': 'bg-red-100 text-red-800 border-red-200',
      'Deleted': 'bg-red-100 text-red-800 border-red-200',
      'Remove': 'bg-red-100 text-red-800 border-red-200',
      'Removed': 'bg-red-100 text-red-800 border-red-200',
      
      // Authentication actions - Purple
      'Login': 'bg-purple-100 text-purple-800 border-purple-200',
      'Logout': 'bg-indigo-100 text-indigo-800 border-indigo-200',
      'FailedLogin': 'bg-red-100 text-red-800 border-red-200',
      'Authentication': 'bg-purple-100 text-purple-800 border-purple-200',
      
      // Status changes - Orange/Yellow
      'Activate': 'bg-emerald-100 text-emerald-800 border-emerald-200',
      'Deactivate': 'bg-amber-100 text-amber-800 border-amber-200',
      'Enable': 'bg-emerald-100 text-emerald-800 border-emerald-200',
      'Disable': 'bg-amber-100 text-amber-800 border-amber-200',
      'Complete': 'bg-emerald-100 text-emerald-800 border-emerald-200',
      'Cancel': 'bg-orange-100 text-orange-800 border-orange-200',
      
      // View/Read actions - Gray
      'View': 'bg-gray-100 text-gray-800 border-gray-200',
      'Read': 'bg-gray-100 text-gray-800 border-gray-200',
      'Access': 'bg-gray-100 text-gray-800 border-gray-200',
      'Open': 'bg-gray-100 text-gray-800 border-gray-200',
      
      // Export/Import actions - Cyan
      'Export': 'bg-cyan-100 text-cyan-800 border-cyan-200',
      'Import': 'bg-teal-100 text-teal-800 border-teal-200',
      'Download': 'bg-cyan-100 text-cyan-800 border-cyan-200',
      'Upload': 'bg-teal-100 text-teal-800 border-teal-200',
    };
    return colors[action as keyof typeof colors] || 'bg-slate-100 text-slate-800 border-slate-200';
  };

  const getEntityTypeIcon = (entityType: string) => {
    switch (entityType.toLowerCase()) {
      case 'user':
        return <User className="h-4 w-4" />;
      case 'workorder':
        return <Activity className="h-4 w-4" />;
      case 'client':
        return <User className="h-4 w-4" />;
      case 'vendor':
        return <User className="h-4 w-4" />;
      default:
        return <Activity className="h-4 w-4" />;
    }
  };

  const formatTimestamp = (timestamp: string) => {
    const date = new Date(timestamp);
    return {
      date: date.toLocaleDateString(),
      time: date.toLocaleTimeString(),
      relative: getRelativeTime(date)
    };
  };

  const getRelativeTime = (date: Date) => {
    const now = new Date();
    const diffInSeconds = Math.floor((now.getTime() - date.getTime()) / 1000);
    
    if (diffInSeconds < 60) return 'Just now';
    if (diffInSeconds < 3600) return `${Math.floor(diffInSeconds / 60)}m ago`;
    if (diffInSeconds < 86400) return `${Math.floor(diffInSeconds / 3600)}h ago`;
    if (diffInSeconds < 2592000) return `${Math.floor(diffInSeconds / 86400)}d ago`;
    return date.toLocaleDateString();
  };

  if (!isOpen || !user) return null;

  return createPortal(
    <div 
      className="fixed top-0 left-0 right-0 bottom-0 bg-gray-500 bg-opacity-75 flex items-center justify-center z-50 p-4" 
      style={{ height: '100vh', width: '100vw' }}
      onClick={onClose}
    >
      <div 
        className="bg-white dark:bg-gray-800 rounded-lg shadow-xl w-full max-w-4xl max-h-[90vh] flex flex-col"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-gray-200 dark:border-gray-700">
          <div>
            <h2 className="text-xl font-semibold text-gray-900 dark:text-gray-100">
              Activity Log - {user.firstName} {user.lastName}
            </h2>
            <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
              {totalCount} total activities
            </p>
          </div>
          <button
            onClick={onClose}
            className="text-gray-400 dark:text-gray-500 hover:text-accent-color dark:hover:text-accent-color-400 transition-colors"
          >
            <X className="h-6 w-6" />
          </button>
        </div>

        {/* Search and Filters */}
        <div className="p-6 border-b border-gray-200 dark:border-gray-700 space-y-4">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400 dark:text-gray-500" />
            <Input
              type="text"
              placeholder="Search activities..."
              value={searchTerm}
              onChange={(e) => handleSearch(e.target.value)}
              className="pl-10"
            />
          </div>
          
          {/* Filter Toggle */}
          <div className="flex items-center justify-between">
            <button
              onClick={() => setShowFilters(!showFilters)}
              className="flex items-center space-x-2 text-sm text-gray-600 dark:text-gray-300 hover:text-gray-900 dark:hover:text-gray-100 transition-colors"
            >
              <Filter className="h-4 w-4" />
              <span>Filters</span>
              <ChevronDown className={`h-4 w-4 transition-transform ${showFilters ? 'rotate-180' : ''}`} />
            </button>
            
            {(actionFilter || entityTypeFilter || startDate || endDate) && (
              <button
                onClick={clearFilters}
                className="text-sm text-accent-color hover:text-accent-color-800 transition-colors"
              >
                Clear Filters
              </button>
            )}
          </div>

          {/* Filter Options */}
          {showFilters && (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 pt-4 border-t">
              {/* Action Filter */}
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Action
                </label>
                <Select value={actionFilter || "all"} onValueChange={(value) => setActionFilter(value === "all" ? "" : value)}>
                  <SelectTrigger>
                    <SelectValue placeholder="All actions" />
                  </SelectTrigger>
                  <SelectContent className="z-[60] bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 shadow-lg">
                    <SelectItem value="all">All actions</SelectItem>
                    {actionsResponse?.data?.map((action) => (
                      <SelectItem key={action} value={action}>
                        {action}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              {/* Entity Type Filter */}
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Entity Type
                </label>
                <Select value={entityTypeFilter || "all"} onValueChange={(value) => setEntityTypeFilter(value === "all" ? "" : value)}>
                  <SelectTrigger>
                    <SelectValue placeholder="All entities" />
                  </SelectTrigger>
                  <SelectContent className="z-[60] bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 shadow-lg">
                    <SelectItem value="all">All entities</SelectItem>
                    {entityTypesResponse?.data?.map((entityType) => (
                      <SelectItem key={entityType} value={entityType}>
                        {entityType}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              {/* Start Date Filter */}
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Start Date
                </label>
                <Input
                  type="date"
                  value={startDate}
                  onChange={(e) => setStartDate(e.target.value)}
                  placeholder="From date"
                />
              </div>

              {/* End Date Filter */}
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  End Date
                </label>
                <Input
                  type="date"
                  value={endDate}
                  onChange={(e) => setEndDate(e.target.value)}
                  placeholder="To date"
                />
              </div>
            </div>
          )}
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-6 min-h-0">
          {isLoading ? (
            <div className="text-center py-8">
              <div className="text-gray-500 dark:text-gray-400">Loading activities...</div>
            </div>
          ) : error ? (
            <div className="text-center py-8">
              <div className="text-red-500">Error loading activities</div>
            </div>
          ) : auditLogs.length === 0 ? (
            <div className="text-center py-8">
              <div className="text-gray-500 dark:text-gray-400">No activities found</div>
            </div>
          ) : (
            <div className="space-y-4">
              {auditLogs.map((log: AuditLog, index: number) => {
                const timestamp = formatTimestamp(log.timestamp);
                return (
                  <div key={log.id} className="relative">
                    {/* Timeline line */}
                    {index < auditLogs.length - 1 && (
                      <div className="absolute left-6 top-12 bottom-0 w-0.5 bg-gray-200"></div>
                    )}
                    
                    <div className="flex items-start space-x-4">
                      {/* Timeline dot */}
                      <div className="flex-shrink-0 w-12 h-12 bg-white border-2 border-gray-200 rounded-full flex items-center justify-center">
                        {getEntityTypeIcon(log.entityType)}
                      </div>
                      
                      {/* Content */}
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center space-x-2 mb-2">
                          <Badge className={`${getActionBadgeColor(log.action)} border`}>
                            {log.action}
                          </Badge>
                          <span className="text-sm font-medium text-gray-900 dark:text-gray-100">
                            {log.entityType}
                          </span>
                          {log.entityId && (
                            <span className="text-sm text-gray-500 dark:text-gray-400">
                              #{log.entityId}
                            </span>
                          )}
                        </div>
                        
                        {log.details && (
                          <p className="text-sm text-gray-700 dark:text-gray-300 mb-2">
                            {log.details}
                          </p>
                        )}
                        
                        <div className="flex items-center space-x-4 text-xs text-gray-500 dark:text-gray-400">
                          <div className="flex items-center space-x-1">
                            <Calendar className="h-3 w-3" />
                            <span>{timestamp.date}</span>
                          </div>
                          <div className="flex items-center space-x-1">
                            <Clock className="h-3 w-3" />
                            <span>{timestamp.time}</span>
                          </div>
                          <span>{timestamp.relative}</span>
                        </div>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>

        {/* Pagination */}
        {totalPages > 1 && (
          <div className="flex items-center justify-between p-6 border-t">
            <div className="text-sm text-gray-500 dark:text-gray-400">
              Showing {((page - 1) * pageSize) + 1} to {Math.min(page * pageSize, totalCount)} of {totalCount} activities
            </div>
            <div className="flex space-x-2">
              <Button
                variant="outline"
                size="sm"
                onClick={() => handlePageChange(page - 1)}
                disabled={page === 1}
              >
                Previous
              </Button>
              <Button
                variant="outline"
                size="sm"
                onClick={() => handlePageChange(page + 1)}
                disabled={page === totalPages}
              >
                Next
              </Button>
            </div>
          </div>
        )}
      </div>
    </div>,
    document.body
  );
};

export default UserAuditModal;
